import { faRefresh } from '@fortawesome/free-solid-svg-icons';

import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { concat } from 'rxjs';

import { AuthService, NotificationService } from '@core/services';

import { RegistrationData } from '@shared/types';
import { equalPasswords, passwordStrengthValidator } from '@shared/validators';

import { environment } from '@env';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false,
})
export class RegistrationComponent implements OnInit {
    registrationForm = new FormGroup(
        {
            email: new FormControl('', [Validators.required, Validators.email]),
            firstName: new FormControl('', [Validators.required]),
            lastName: new FormControl('', [Validators.required]),
            password1: new FormControl('', [
                Validators.required,
                Validators.minLength(environment.minPasswordLength),
                passwordStrengthValidator(),
            ]),
            password2: new FormControl('', [Validators.required]),
        },
        {
            validators: equalPasswords('password1', 'password2'),
        },
    );
    silverfinLogin = environment.endpoints.silverfinLogin;
    busy = false;
    loadingIcon = faRefresh;
    minPasswordLength = environment.minPasswordLength;
    specialCharacters = environment.passwordSpecialCharacters;

    constructor(private authService: AuthService, private notificationService: NotificationService, private router: Router) {}

    ngOnInit(): void {
        if (this.authService.activeUser.getValue() != undefined) {
            this.router.navigate([this.authService.popLoginRedirect()]);
        }
    }

    onSubmit(): void {
        this.registrationForm.markAllAsTouched();

        if (!this.registrationForm.valid) {
            return;
        }

        this.busy = true;
        this.authService.register(this.registrationForm.value as RegistrationData).subscribe({
            next: () => {
                this.router.navigate(['/']);
                this.busy = false;
                this.notificationService.successMessage(
                    $localize`:@@login.error.verifyEmail:Je account werd aangemaakt. Check je inbox om je e-mailadres te verifiëren.`,
                );
            },
            error: (err) => {
                console.error('Something went wrong while registering the new user:', err);
                let showedFieldErrors = false;

                if (err?.status === 400 && err?.error) {
                    const backendErrors = err.error as Record<string, string[] | string>;
                    const fieldMap: Record<string, 'email'> = {
                        email: 'email',
                    };

                    Object.entries(fieldMap).forEach(([backendKey, controlKey]) => {
                        const message = Array.isArray(backendErrors[backendKey])
                            ? (backendErrors[backendKey] as string[])[0]
                            : (backendErrors[backendKey] as string | undefined);
                        if (message) {
                            const control = this.registrationForm.controls[controlKey];
                            const currentErrors = control.errors ?? {};
                            control.setErrors({ ...currentErrors, backend: message });
                            control.markAsTouched();
                            showedFieldErrors = true;
                        }
                    });

                    // Surface non-field errors only if no field-specific errors were shown
                    if (!showedFieldErrors && Array.isArray(backendErrors.non_field_errors) && backendErrors.non_field_errors.length) {
                        this.notificationService.errorMessage(backendErrors.non_field_errors[0]);
                        this.busy = false;
                        return;
                    }
                }

                if (!showedFieldErrors) {
                    const msg = $localize`:@@error.registration:Registratie gefaald, probeer later opnieuw.`;
                    this.notificationService.errorMessage(msg);
                }
                this.busy = false;
            },
        });
    }
}
